import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Flex, Text, Box, Button, Image } from 'rebass';
import styled from '@emotion/styled';
import { FiChevronRight, FiChevronDown } from 'react-icons/fi';
import { sortableContainer, sortableElement, sortableHandle } from 'react-sortable-hoc';
import { MdDragIndicator } from 'react-icons/md';
import { useParams } from 'react-router-dom';
import { BiBox } from "react-icons/bi";
import { FaExclamationTriangle, FaRegEdit } from "react-icons/fa";
import arrayMove from 'array-move';
import { patch } from 'utils/api';
import { DateTime } from 'luxon';
import Default_PreviewPhoto from 'stories/assets/Default_PreviewPhoto.png';
import { CgArrowRightR } from "react-icons/cg";
import RadioToggle from './RadioToggle';
import { AddToChapterSection } from 'stories/list/item/index';
import useSWR from 'swr';
import StoryPageList from 'story-pages/list/index';
import Modal from 'app/modal/v2';
import useHistory from 'app/hooks/history';

export const DragHandle = sortableHandle(() => (
    <Text
        className="handle"
        sx={{ cursor: 'all-scroll' }}
        color="#64748B"
        as={MdDragIndicator}
        size={24}
        minWidth={24}
    // ml="auto"
    />
));

export const SortableContainer = sortableContainer(({ children }) => <ul>{children}</ul>);

export const Paragraph = styled(Text)`
    white-space: normal;
    word-break: break-word;
    overflow-wrap: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
`

function RenderStorySection({ noChapterHeading, firstBookId, setSelectedStory, ...val }) {

    let parsedCreated = DateTime.fromISO((val.createdAt || '').split('T')[0]);
    parsedCreated = parsedCreated.toFormat('LLL d, yyyy');

    const [isDisabled, setIsDisabled] = useState(val.state !== 'enabled')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { id: journalId } = useParams()
    const { revalidate } = useSWR(firstBookId ? `/v3/books/${firstBookId}/book_stories` : null);

    useEffect(() => {
        setIsDisabled(val.state !== 'enabled')
    }, [val.state])

    const onToggle = async (toggled) => {
        setIsSubmitting(true)
        const { data, status } = await patch(
            `/v3/books/${firstBookId}/book_stories/${val?.id}`,
            {
                data: {
                    attributes: { state: toggled ? 'enabled' : 'disabled' },
                },
            }
        )

        if (status === 200) {
            setIsSubmitting(false)
            setIsDisabled(!toggled)
        }
    }

    return (
        <Flex
            key={val.id}
            alignItems="center"
            bg="#fff"
            sx={{
                borderRadius: '10px',
                boxShadow: '4px 4px 4px 0 rgba(133, 79, 155, 0.05)',
                gap: '10px',
                opacity: isDisabled ? 0.5 : 1
            }}
            m="10px"
            p={[1, "10px 20px"]}
        >
            {!noChapterHeading && (isDisabled ? <Box minWidth={24} /> : <DragHandle />)}
            <Image onClick={setSelectedStory} src={val?.coverPhoto || Default_PreviewPhoto} width="54px" height="54px" sx={{ borderRadius: '10px', objectFit: 'cover', cursor: 'pointer' }} />
            <Box onClick={setSelectedStory} sx={{ cursor: 'pointer' }}>
                <Paragraph variant="paragraph" width={['100px', 'fit-content', 'fit-content']}>
                    {val.name}
                </Paragraph>
                <Paragraph variant="paragraph" color="var(--LightGreyText, #8F8F8F)">
                    {parsedCreated}
                </Paragraph>
            </Box>
            <Flex ml="auto" alignItems="center" sx={{ gap: '10px' }}>
                <AddToChapterSection story={{}} revalidate={revalidate} storyID={val?.storyId} chapter_id={val?.chapterId} journalId={journalId}>
                    <Flex flexDirection="column" alignItems="center" sx={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }} >
                        <Text variant="paragraph" color={isDisabled ? "#8F8F8F" : "#9B59B6"} fontWeight={500} fontSize={14} >
                            Chapter
                        </Text>
                        <CgArrowRightR color={isDisabled ? "#8F8F8F" : "#9B59B6"} size={24} />
                    </Flex>
                </AddToChapterSection>
                {!noChapterHeading && <Text bg="rgba(0, 0, 0, 0.05)" height='59px' width="1px" />}
                {!noChapterHeading && <RadioToggle
                    isSubmitting={isSubmitting}
                    initial={!isDisabled}
                    label={<Text variant="paragraph" color={isDisabled ? "#8F8F8F" : "#9B59B6"} fontWeight={500} fontSize={14} >
                        {isDisabled ? "Excluded" : "In Book"}
                    </Text>}
                    onToggle={onToggle}
                />}
            </Flex>
        </Flex>
    )
}


const SortableItem = sortableElement((props) => (
    <li>
        <RenderStorySection {...props} />
    </li>
));

function StoriesSection({ heading, stories, firstBookId }) {
    const [isOpen, setIsOpen] = useState(stories.length > 0);
    const [list, setList] = useState([])
    const [selectedStory, setSelectedStory] = useState(null)
    const { push } = useHistory();

    const onSortEnd = ({ oldIndex, newIndex }) => {
        const next = arrayMove(list, oldIndex, newIndex);
        const updatedList = next.map((ch, index) => ({ ...ch, position: index }))

        setList(updatedList)

        updatedList.map(((tag) => patch(
            `/v3/books/${firstBookId}/book_stories/${tag?.id}`,
            {
                data: {
                    attributes: { position: tag.position },
                },
            }
        )
        ))
    };

    useEffect(() => {
        setList(stories.sort((a, b) => a.position - b.position))
        setIsOpen(stories.length > 0)
    }, [stories.length])

    let height = '0px';
    const noChapterHeading = heading === "Stories that need a Chapter"
    if (isOpen) height = noChapterHeading ? "323px" : `max-content`;

    if (noChapterHeading && list.length === 0) {
        return null
    }

    return (
        <Box
            mb={2}
            sx={{
                borderRadius: '10px',
                border: noChapterHeading && '1.5px solid #FF9294',
                background: noChapterHeading ? 'linear-gradient(0deg, rgba(248, 238, 238, 0.50) 0%, rgba(248, 238, 238, 0.50) 100%), #FFF' :
                    'linear-gradient(0deg, rgba(245, 238, 248, 0.50) 0%, rgba(245, 238, 248, 0.50) 100%), #FFF',
                boxShadow: '4px 4px 4px 0 rgba(133, 79, 155, 0.05)',
            }}
        >
            {selectedStory &&
                <Modal
                    closeButtonLeft
                    aboveTooltip
                    shadeProps={{ pt: [0, 1] }}
                    onClose={() => setSelectedStory(null)}
                    maxWidth={'95vw'}
                    isActive={false}
                >
                    <Modal.InnerWrap>
                        <Modal.Title style={{
                            color: '#000',
                            fontFamily: 'Poppins',
                            fontSize: '22px',
                            fontWeight: '400',
                            lineHeight: '165%',
                        }}>Story Preview</Modal.Title>
                    </Modal.InnerWrap>
                    <Box mt={3}>
                        <StoryPageList storyId={selectedStory?.storyId} editing />
                        <Button onClick={() => push(`/story/${selectedStory?.storyId}/edit`)} width="max-content" backgroundColor="#9B59B6" sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: "5px",
                            margin: 'auto', color: '#fff', borderRadius: '99px', position: 'absolute',
                            padding: '0px 18px',
                            top: '15px',
                            right: '5px',
                        }}>
                            <FaRegEdit color="#fff" size={24} />
                            <Text>
                                Edit Story
                            </Text>
                        </Button>
                    </Box>
                </Modal>}
            <Flex
                justifyContent="space-between"
                alignItems="center"
                p={2}
                sx={noChapterHeading ? {
                    borderRadius: isOpen ? '10px 10px 0 0' : '10px',
                    background: '#FFE5EA',
                } : {
                    borderRadius: '10px 10px 0 0',
                    border: '1px solid #F5EEF8',
                    background: '#FFF'
                }}
            >
                <Flex alignItems="center" sx={{ gap: 2 }}>
                    {noChapterHeading && <FaExclamationTriangle color="#FF9294" size={24} />}
                    <Text
                        sx={{
                            fontSize: '16px',
                            lineHeight: '120%',
                            color: 'black',
                            fontWeight: 600,
                            textAlign: 'left',
                        }}
                    >
                        {heading} {`(${list.length})`}
                    </Text>
                </Flex>
                <Text
                    color="black"
                    as={isOpen ? FiChevronDown : FiChevronRight}
                    size={24}
                    sx={{ textDecorationLine: 'underline', cursor: 'pointer' }}
                    onClick={() => setIsOpen(!isOpen)}
                />
            </Flex>
            <Box sx={{ transition: 'height 0.6s', height, overflowY: 'auto' }}>
                {list?.length === 0 ?
                    <Flex
                        m={"10px"}
                        alignItems={"center"}
                        p="10px 20px" sx={{
                            borderRadius: '10px',
                            border: '1.5px dashed var(--LightGreyText, #8F8F8F)',
                            background: '#FFF',
                            gap: 2
                        }}>
                        <BiBox size={24} color="#8F8F8F" />
                        <Text
                            color="var(--LightGreyText, #8F8F8F)"
                            variant={"paragraph"}
                        >
                            No stories in this chapter yet
                        </Text>
                    </Flex>
                    :
                    <SortableContainer useDragHandle onSortEnd={onSortEnd}>
                        {(list || []).map((val, i) => (
                            <SortableItem firstBookId={firstBookId} noChapterHeading={noChapterHeading} key={`item-${val.storyId}`} setSelectedStory={() => {
                                setSelectedStory(val)
                            }} {...val} index={i} />
                        ))}
                    </SortableContainer>}
            </Box>
        </Box>
    );
}

export default StoriesSection;